import { CheckOutlined, CloseOutlined, DeleteOutlined, RedoOutlined } from '@ant-design/icons';
import { Button, Collapse, Popconfirm, message } from 'antd';
import { customAlphabet } from 'nanoid/non-secure';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { generatePath, redirect, useActionData, useLoaderData, useNavigate, useSubmit } from 'react-router-dom';
import { createVendor } from '../../api/vendor/create-vendor.js';
import { SmallSection } from '../../components/design';
import { ValidationForm as Form, VendorAddress, VendorContact, VendorDetails } from '../../components/form';
import { VendorContactRoleSelect } from '../../components/inputs/vendor-contact-roles-select.jsx';
import { Page } from '../../components/page';
import { ValidationError } from '../../library/classes/exceptions/index.js';
import { getCountryById } from '../../library/helpers/country.js';
import { getEntityById } from '../../library/helpers/entity.js';
import { getLanguageIdByCode } from '../../library/helpers/language.js';
import { CancelButton } from '../../ui';
const { Panel } = Collapse
const nanoid = customAlphabet('1234567890', 4)
const numericNanoid = () => parseInt(nanoid())

const LOCAL_STORAGE_KEY = 'newVendorDetails'


const getSupportedLanguagesByCountryId = (countryId) => {
	if (!countryId) {
		return []
	}
	const country = getCountryById(countryId)
	const entity = getEntityById(country.default_entity_id)
	//Supported languages
	return entity?.default_language ?? [getLanguageIdByCode('en')] // English is default support language
}

const getDefaultVendorContactRole = (contacts) => contacts.length === 1 ? 'primary' : undefined

const normalizeVendorContacts = (contacts = []) => contacts.map((contact, index) => ({
	...contact,
	role_id: contact.role_id ?? (contacts.length === 1 && index === 0 ? 'primary' : undefined)
}))

const createVendorFormData = ({
	vendorDetails = {},
	addresses = [],
	vendor_contacts = [],
	primaryAddressId,
	invoiceAdddresId,
	deliveryAddressId,
	enquiryContactId,
	orderContactId
}) => ({
	vendorDetails,
	addresses: addresses.length ? addresses : [],
	vendor_contacts: vendor_contacts.length ? normalizeVendorContacts(vendor_contacts) : [],
	primaryAddressId,
	invoiceAdddresId,
	deliveryAddressId,
	enquiryContactId,
	orderContactId
})

const persistVendorFormData = (data) => {
	localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data))
}

const getFormErrors = (errors) => errors?.reduce((acc, curr) => Object.assign(acc, curr), {}) ?? {}

const VendorCreate = ({ title }) => {
	const [form] = Form.useForm()
	const loaderData = useLoaderData()
	const [vendorState, setVendorState] = useState(() => createVendorFormData(loaderData))
	const { vendorDetails, addresses, vendor_contacts } = vendorState
	const [contactLanguageOptions, setContactLanguageOptions] = useState(getSupportedLanguagesByCountryId(vendorDetails?.country_id))


	const formValidationStatus = useRef({
		vendorDetails: false,
		addresses: {},
		vendor_contacts: {},
	})
	const actionData = useActionData()
	const { errors = {} } = actionData || {}
	const { addressErrors = [], contactErrors = [], vendorErrors = {} } = errors
	const submit = useSubmit()
	const navigate = useNavigate()

	const updateFormData = (updater, persist = false) => {
		setVendorState(current => {
			const next = typeof updater === 'function' ? updater(current) : updater
			if (!next || next === current) {
				return current
			}
			if (persist) {
				persistVendorFormData(next)
			}
			return next
		})
	}

	const defaultVendorDetails = {
		using_portal: true,
		discount: 0,
		vendor_type: ['Item']
	}

	const handleVendorDetailsSubmit = async (changed, allValues) => {
		//Check if country is changed
		if (changed.country_id) {
			//Supported languages
			setContactLanguageOptions(getSupportedLanguagesByCountryId(changed.country_id))
		}
		updateFormData(current => ({
			...current,
			vendorDetails: { ...allValues }
		}), true)
	}
	const handleVendorAddressSubmit = async (id, address) => {
		updateFormData(current => {
			const addressIndex = current.addresses.findIndex(a => a.id === id)
			if (addressIndex === -1) {
				return current
			}
			return {
				...current,
				addresses: current.addresses.toSpliced(addressIndex, 1, address)
			}
		}, true)
	}

	const handleVendorContactSubmit = async (id, contact) => {
		updateFormData(current => {
			const contactIndex = current.vendor_contacts.findIndex(a => a.id === id)
			if (contactIndex === -1) {
				return current
			}
			return {
				...current,
				vendor_contacts: current.vendor_contacts.toSpliced(contactIndex, 1, contact)
			}
		}, true)
	}

	const getAddressActions = (id) => {
		return ([
			<Popconfirm
				key="delete"
				title="Are you sure to delete this address?"
				onConfirm={() => {
					updateFormData(current => {
						const index = current.addresses.findIndex(a => a.id === id)
						if (index === -1) {
							return current
						}
						return {
							...current,
							addresses: current.addresses.toSpliced(index, 1)
						}
					}, true)
				}}
			>
				<Button type="primary" shape="circle" icon={<DeleteOutlined />} danger />
			</Popconfirm>
		])
	}
	const getContactAction = (id) => {
		return ([
			<Popconfirm
				key="delete"
				title="Are you sure to delete this contact?"
				onConfirm={() => {
					updateFormData(current => {
						const index = current.vendor_contacts.findIndex(a => a.id === id)
						if (index === -1) {
							return current
						}
						return {
							...current,
							vendor_contacts: current.vendor_contacts.toSpliced(index, 1)
						}
					}, true)
				}}
			>
				<Button type="primary" shape="circle" icon={<DeleteOutlined />} danger />
			</Popconfirm>
		])
	}

	const handleAddNewAddress = () => {
		updateFormData(current => ({
			...current,
			addresses: [...current.addresses, {
				id: numericNanoid(),
				country_id: current.vendorDetails?.country_id
			}]
		}), true)
	}

	const handleAddNewContact = () => {
		const country_id = form.getFieldValue('country_id')
		const country = getCountryById(country_id)
		updateFormData(current => {
			const nextContacts = [...current.vendor_contacts, {
				id: numericNanoid(),
				phone_country_code: country?.phone_number_code,
				mobile_country_code: country?.phone_number_code,
				language_id: current.vendorDetails?.language_id
			}]
			return {
				...current,
				vendor_contacts: nextContacts.map((contact, index) => ({
					...contact,
					role_id: contact.role_id ?? (nextContacts.length === 1 && index === 0 ? 'primary' : undefined)
				}))
			}
		}, true)
	}
	const handleResetFields = async () => {
		const resetContact = { id: numericNanoid(), role_id: 'primary' }
		const resetData = {
			vendorDetails: {},
			addresses: [{ id: numericNanoid() }],
			vendor_contacts: [resetContact],
			primaryAddressId: undefined,
			invoiceAdddresId: undefined,
			deliveryAddressId: undefined,
			enquiryContactId: undefined,
			orderContactId: undefined
		}

		await form.resetFields()
		form.setFieldsValue(defaultVendorDetails)
		updateFormData(resetData, true)
	}

	const handleFormSubmit = async () => {
		//Check basic validations 
		// const isValid = formValidationStatus.vendorDetails && formValidationStatus.addresses
		// console.log(isValid);
		if (Object.values(vendorState.addresses).length == 0) {
			return message.error('Please add atleast one address')
		}
		if (Object.values(vendorState.vendor_contacts).length == 0) {
			return message.error('Please add atleast one contact')
		}

		submit({
			...vendorState,
			action: 'saveVendor'
		}, {
			method: 'post',
			encType: 'application/json'
		})
	}
	const changeFormsHandler = (formName, { changedFields, forms }) => {
		if (formName !== 'vendorDetails' || !changedFields.find(({ name }) => name.includes('country_id'))) {
			return
		}

		const country_id = forms.vendorDetails.getFieldValue('country_id')
		const language_id = forms.vendorDetails.getFieldValue('language_id')
		const country = getCountryById(country_id)
		for (const fkey in forms) {
			if (!fkey.startsWith('contact')) {
				continue
			}

			if (forms[fkey].getFieldValue('mobile_country_code') == undefined || forms[fkey].getFieldValue('mobile_number') == undefined) {
				forms[fkey].setFieldValue('mobile_country_code', country?.phone_number_code)
			}
			if (forms[fkey].getFieldValue('phone_country_code') == undefined || forms[fkey].getFieldValue('phone_number') == undefined) {
				forms[fkey].setFieldValue('phone_country_code', country?.phone_number_code)
			}
			forms[fkey].setFieldValue('language_id', language_id)
		}
	}

	return (
		<Page className='vendors-detail' title={title} >
			<Form.Provider onFormChange={changeFormsHandler}>
					<Collapse defaultActiveKey={['1']} expandIconPosition="end">
						<Panel header={<><img src="/icons/headericons/vendordetails.svg" style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: '8px' }} />Vendor Details</>} key="1">
							<VendorDetails
							form={form}
							initialValues={
								Object.keys(vendorDetails).length == 0 ? defaultVendorDetails : vendorDetails
							}
							onValuesChange={handleVendorDetailsSubmit}
							getValidationStatus={isValid => formValidationStatus.current.vendorDetails = isValid}
								errors={vendorErrors}
							/>
						</Panel>
					</Collapse>
					<Collapse defaultActiveKey={['1']} expandIconPosition="end">
						<Panel header={<><img src="/icons/headericons/address.svg" style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: '8px' }} />Addresses</>} key="1" >
							{addresses.map((address, index) => (
								<SmallSection
									key={address.id}
									actions={getAddressActions(address.id)}
								>
									<VendorAddress
										index={address.id}
										initialValues={address}
										onValuesChange={(_, data) => handleVendorAddressSubmit(address.id, { ...data, id: address.id })}
										getValidationStatus={isValid => formValidationStatus.current.addresses[address.id] = isValid}
										errors={getFormErrors(addressErrors?.[index])}
									/>
								</SmallSection>
							))}
							<Button onClick={handleAddNewAddress}>Add New Address</Button>
						</Panel>
					</Collapse>
					<Collapse defaultActiveKey={['1']} expandIconPosition="end">
						<Panel header={<><img src="/icons/headericons/contacts.svg" style={{ width: '1.25rem', verticalAlign: 'sub', marginRight: '8px' }} />Contacts</>} key="1" >
							{vendor_contacts.map((contact, index) => (
								<SmallSection
									key={contact.id}
									actions={getContactAction(contact.id)}
									header={(() => {
										const roleError = getFormErrors(contactErrors?.[index])?.role_id
										return (
											<>
												<VendorContactRoleSelect
													contactId={contact.id}
													role={contact.role_id ?? getDefaultVendorContactRole(vendor_contacts)}
													onChange={(role, contactId) => handleVendorContactSubmit(contactId, { ...contact, role_id: role })}
													disabled={vendor_contacts.length === 1}
												/>
												{roleError ? <div style={{ color: '#ff4d4f', marginTop: 4 }}>{roleError}</div> : null}
											</>
										)
									})()}
								>
									<VendorContact
										index={contact.id}
										initialValues={contact}
										onValuesChange={(_, data) => handleVendorContactSubmit(contact.id, { ...data, id: contact.id })}
										allowedLanguages={contactLanguageOptions}
										getValidationStatus={isValid => formValidationStatus.current.vendor_contacts[contact.id] = isValid}
										errors={getFormErrors(contactErrors?.[index])}
									/>
								</SmallSection>
							))}
							<Button onClick={handleAddNewContact}>Add New Contact</Button>
						</Panel>
					</Collapse>
					<div className="actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
						<Button onClick={handleResetFields} type="primary" icon={<RedoOutlined />}>Reset Fields</Button>
						<Button onClick={handleFormSubmit} type="primary" icon={<CheckOutlined />}>Create</Button>
						<CancelButton onClick={() => {
							if (confirm("Unsaved changes will be lost") == true) {
							navigate(-1)
						}
					}} icon={<CloseOutlined />} >Cancel</CancelButton>
				</div>
			</ Form.Provider>
		</Page>
	)
}
VendorCreate.propTypes = {
	title: PropTypes.string
}

VendorCreate.Actions = {
	saveVendor: async ({ data }) => {
		const { vendorDetails, vendor_contacts, addresses } = data
		const formElementsMap = new Map()
		addresses.forEach(({ id }, i) => formElementsMap.set(`address-${i}`, `address-${id}`))
		vendor_contacts.forEach(({ id }, i) => formElementsMap.set(`contact-${i}`, `contact-${id}`))
			try {
				const payload = {
					...vendorDetails,
					vendor_contacts: vendor_contacts.map((c, index) => ({
						...c,
						role_id: c.role_id ?? (vendor_contacts.length === 1 && index === 0 ? 'primary' : undefined),
						id: undefined
					})),
				addresses: addresses.map(c => ({
					...c,
					id: undefined
				}))
			}

			const { message: msg, data: { id } } = await createVendor(payload)
			localStorage.removeItem(LOCAL_STORAGE_KEY)
			message.success(msg)
			return redirect(generatePath('/vendors/:id', {
				id
			}))
		}
		catch (error) {
			message.error(error.message)
			if (error instanceof ValidationError) {
				//message.error(error.message)
				const formattedErrors = {
					addresses: [],
					vendor_contacts: []
				}

				Object.entries(error.errors).forEach(([k, v]) => {
					//['vendor_contact',0,[]]
					const k_path = k.split('.')
					if (k_path.length === 3) {
						if (k_path[0] === 'addresses') {
							if (formattedErrors.addresses[k_path[1]] == null) formattedErrors.addresses[k_path[1]] = []
							formattedErrors.addresses[k_path[1]].push({
								[k_path[2]]: v
							})
						}
						else if (k_path[0] === 'vendor_contacts') {
							///Send it to vendor_contacts reducer
							if (formattedErrors.vendor_contacts[k_path[1]] == null) formattedErrors.vendor_contacts[k_path[1]] = []
							formattedErrors.vendor_contacts[k_path[1]].push({
								[k_path[2]]: v
							})
						}
					}
					else {
						formattedErrors[k] = v
					}
				})
				let errorFormElementId = ''
				const { addresses: addressErrors = [], vendor_contacts: contactErrors = [], ...vendorErrors } = formattedErrors
				if (Object.keys(vendorErrors)?.length > 0) {
					errorFormElementId = 'vendorDetails'
				}
				else if (addressErrors.length) {
					const errorIndex = Math.min(...Object.keys(addressErrors).map(Number))
					errorFormElementId = formElementsMap.get(`address-${errorIndex}`)
				}
				else if (contactErrors.length) {
					const errorIndex = Math.min(...Object.keys(contactErrors).map(Number))
					errorFormElementId = formElementsMap.get(`contact-${errorIndex}`)
				}
				if (errorFormElementId && document.querySelector(`#${errorFormElementId}`)) {
					document.querySelector(`#${errorFormElementId}`).scrollIntoView({ behavior: 'smooth', block: 'start' })
				}
				return {
					errors: { vendorErrors, addressErrors, contactErrors }
				}
			}

		}
		return false
	}
}

VendorCreate.Loader = async () => {
	let { addresses, vendor_contacts, vendorDetails = {}, primaryAddressId, invoiceAdddresId, deliveryAddressId, enquiryContactId, orderContactId } = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || {}

	//Always maintain atleast one address and one contact
	if (!addresses || addresses.length === 0) {
		addresses = [{ id: numericNanoid() }]
		primaryAddressId = addresses[0].id
	}
	if (!vendor_contacts || vendor_contacts.length === 0) {
		vendor_contacts = [{ id: numericNanoid() }]
		// primaryContactId = vendor_contacts[0].id
	}

	return {
		vendorDetails,
		addresses,
		vendor_contacts,
		primaryAddressId,
		invoiceAdddresId,
		deliveryAddressId,
		enquiryContactId,
		orderContactId
	}
}
export default VendorCreate
